import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { token, updateToken, updateUser, user } = useAuth();
  const navigate = useNavigate();

  // Password requirements check
  const requirements = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*]/.test(newPassword)
  };

  const allRequirementsMet = Object.values(requirements).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to change password');
      }

      // Update token and user
      updateToken(data.token);
      updateUser({ ...user, mustChangePassword: false });
      
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.icon}>🔐</div>
          <h1 style={styles.title}>Password Change Required</h1>
          <p style={styles.subtitle}>You must create a new password to continue</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter temporary password from email"
              required
            />
            <p style={styles.hint}>Enter the temporary password from your email</p>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.input}
              placeholder="Create new password"
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                ...styles.input,
                borderColor: confirmPassword ? (passwordsMatch ? '#22c55e' : '#ef4444') : '#e5e7eb'
              }}
              placeholder="Confirm new password"
              required
            />
          </div>

          {/* Password Requirements */}
          <div style={styles.requirements}>
            <p style={styles.reqTitle}>Password Requirements:</p>
            <div style={styles.reqGrid}>
              <RequirementItem met={requirements.length} text="8+ characters" />
              <RequirementItem met={requirements.uppercase} text="Uppercase letter" />
              <RequirementItem met={requirements.lowercase} text="Lowercase letter" />
              <RequirementItem met={requirements.number} text="Number" />
              <RequirementItem met={requirements.special} text="Special char (!@#$%^&*)" />
              <RequirementItem met={passwordsMatch} text="Passwords match" />
            </div>
          </div>

          <button 
            type="submit" 
            style={{
              ...styles.button,
              opacity: (allRequirementsMet && passwordsMatch) ? 1 : 0.5,
              cursor: (allRequirementsMet && passwordsMatch) ? 'pointer' : 'not-allowed'
            }}
            disabled={loading || !allRequirementsMet || !passwordsMatch}
          >
            {loading ? 'Changing Password...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const RequirementItem = ({ met, text }) => (
  <div style={styles.reqItem}>
    <span style={{ color: met ? '#22c55e' : '#9ca3af' }}>
      {met ? '✓' : '○'}
    </span>
    <span style={{ color: met ? '#374151' : '#9ca3af' }}>{text}</span>
  </div>
);

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #ef4444, #f97316)',
    padding: '1rem'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  icon: {
    fontSize: '3rem',
    marginBottom: '0.5rem'
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    color: '#1e3a5f',
    margin: '0 0 0.5rem 0'
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '0.9rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '0.75rem',
    borderRadius: '8px',
    fontSize: '0.9rem',
    textAlign: 'center'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem'
  },
  label: {
    fontWeight: '500',
    color: '#374151',
    fontSize: '0.9rem'
  },
  input: {
    padding: '0.75rem',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s'
  },
  hint: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    margin: 0
  },
  requirements: {
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    padding: '1rem'
  },
  reqTitle: {
    fontWeight: '500',
    color: '#374151',
    fontSize: '0.85rem',
    margin: '0 0 0.5rem 0'
  },
  reqGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.3rem'
  },
  reqItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    fontSize: '0.8rem'
  },
  button: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    marginTop: '0.5rem',
    transition: 'opacity 0.2s'
  }
};

export default ChangePassword;
